import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  MedicationAdministered,
  PatientReportData,
  QuestionResponse,
} from "../../ts/types";
import {
  fetchMedicationsByIds,
  fetchSymptomsByIds,
  getScreeningQuestions,
} from "../../services/masters";
import { RootState } from "../../store";
import {
  getAge,
  getFormattedDateTime,
  getFormattedDob,
  getPatientFullName,
  getBloodGlucoseUnit,
  getTemperatureUnit,
  addLocalOffsetToTimestamp,
  getFormattedDate,
} from "../../utils";
import { InsuranceStatus, MultipleChoice } from "../../ts/enums";
import CommonMaster from "../../ts/models/CommonMaster";
import ClosableBox from "../../components/Box/ClosableBox";
import DataField from "../../components/Box/DataField";
import classes from "./PatientDataDrawer.module.css";
import Event from "../../ts/models/Event";
import Card from "../../components/Card/Card";
import Medication from "../../ts/models/Medication";
import Patient from "../../ts/models/Patient";
import ScreeningQuestion from "../../ts/models/ScreeningQuestion";

type Props = {
  patientReportData: PatientReportData;
  event?: Event;
  checkInUpdateTime?: number;
  updatedPatientData?: Patient;
};

const PatientDataDrawer: React.FC<Props> = ({
  patientReportData,
  event,
  checkInUpdateTime,
  updatedPatientData,
}) => {
  const { genders, dispositions, services, incentives, ethnicGroups, races } =
    useSelector((state: RootState) => state.masters);
  const [symptoms, setSymptoms] = useState<CommonMaster[]>([]);
  const [medicationData, setMedicationData] =
    useState<Map<string, Medication>>();
  const [screeningQuestions, setScreeningQuestions] = useState<
    Map<string, ScreeningQuestion>
  >(new Map());

  const { patient, encounter } = patientReportData;
  const { dob, gender } = patient;
  const encounterDetails = encounter?.encounterDetails;
  const discharge = encounter?.discharge;
  const dischargeIncentives: { description: string; serialNumber: string }[] =
    [];

  discharge?.incentives?.forEach((incentiveData) => {
    const masterIncentive = incentives.find(
      (incentive) => incentiveData.incentive === incentive.id,
    );
    if (masterIncentive) {
      dischargeIncentives.push({
        description: masterIncentive.description,
        serialNumber: incentiveData.serialNumber,
      });
    }
  });

  useEffect(() => {
    const symptoms =
      encounterDetails
        ?.map((encounterDetail) => encounterDetail.observations?.symptoms)
        .flat()
        .filter((ele) => !!ele) || [];

    const getSymptoms = async () => {
      if (symptoms.length) {
        const { status, data } = await fetchSymptomsByIds(symptoms);
        if (status === 200) {
          setSymptoms(data);
        }
      }
    };

    if (symptoms.length) {
      getSymptoms();
    }
  }, [encounterDetails]);

  const getGender = (gender: string): string => {
    const genderMaster = genders.find((gen) => gen.id === gender);

    if (genderMaster) {
      return genderMaster.description;
    }
    return "";
  };

  const getEthnicity = (ethnicity: string): string => {
    const ethnicGroupsMaster = ethnicGroups.find((eth) => eth.id === ethnicity);

    if (ethnicGroupsMaster) {
      return ethnicGroupsMaster.description;
    }
    return "";
  };

  const getRace = (race: string): string => {
    const racesMaster = races.find((e) => e.id === race);

    if (racesMaster) {
      return racesMaster.description;
    }
    return "";
  };

  const getDisposition = (): string => {
    const disposition = dispositions.find(
      (disposition) => disposition.id === encounter?.discharge?.disposition,
    );

    if (disposition) {
      return disposition.description;
    }

    return "";
  };

  const getService = (serviceId: string) => {
    return services.find((service) => service.id === serviceId)?.name || "";
  };

  const getSymptoms = (symptomIds?: string[]) => {
    return symptoms
      .filter((symptom) => symptomIds?.includes(symptom.id))
      .map((symptom) => symptom.description)
      .join(", ");
  };

  useEffect(() => {
    const medications =
      encounterDetails
        ?.map(
          (encounterDetail) =>
            encounterDetail.administration?.medicationAdministered,
        )
        .flat()
        .filter((ele) => !!ele) || [];

    const getMedicationData = async () => {
      const medicationIds = medications.map(
        (medication) => medication?.medication,
      );

      if (medicationIds.length) {
        const { status, data } = await fetchMedicationsByIds(medicationIds);
        if (status === 200) {
          let medicationData = new Map<string, Medication>();
          data.forEach((medication: Medication) => {
            medicationData.set(medication.id, medication);
          });

          setMedicationData(medicationData);
        }
      }
    };

    if (medications.length) {
      getMedicationData();
    }

    const screeningResponse =
      encounterDetails
        ?.map(
          (encounterDetail) =>
            encounterDetail.administration?.screeningResponses,
        )
        .flat()
        .filter(Boolean) || [];
    const questionIds: string[] = screeningResponse.map(
      (item: any) => item.questionId,
    );

    const fetchScreeningQueData = async () => {
      const screeningQueIds = Array.from(new Set([...questionIds]));

      if (screeningQueIds.length) {
        const res = await getScreeningQuestions(screeningQueIds);
        if (res.status) {
          let screeningData = new Map<string, ScreeningQuestion>();
          res.data.forEach((screeningQues: ScreeningQuestion) => {
            screeningData.set(screeningQues.id, screeningQues);
          });

          setScreeningQuestions(screeningData);
        }
      }
    };
    if (questionIds.length) {
      fetchScreeningQueData();
    }
  }, [encounterDetails]);

  const getMedicationName = (medicationId: string) => {
    if (medicationData?.get(medicationId)) {
      return medicationData.get(medicationId)?.name;
    }
    return "";
  };

  const getMedication = (
    medicationAdministeredData?: MedicationAdministered[],
  ) => {
    return (
      <DataField
        name='Medication:'
        data={
          <>
            {medicationAdministeredData?.length ? (
              medicationAdministeredData.map((medicationAdministered) => (
                <div key={medicationAdministered.medication}>
                  <div>{`${getMedicationName(
                    medicationAdministered.medication || "",
                  )} ${medicationAdministered.dosage}`}</div>
                  <div>{medicationAdministered?.administeredBy}</div>
                  <div className={classes.administeredTime}>
                    {getFormattedDateTime(
                      medicationAdministered.administeredTime || 0,
                    )}
                  </div>
                </div>
              ))
            ) : (
              <>None</>
            )}
          </>
        }
      />
    );
  };

  const getResponse = (resIdx: string, id: string) => {
    const responses = resIdx.split(",");
    const options = screeningQuestions.get(id)?.options || [];
    if (responses.length) {
      return responses
        .map((resIndex) => options[Number(resIndex)]?.text)
        .join(", ");
    } else {
      return "";
    }
  };

  return (
    <div className={classes.main}>
      <div className={classes.container}>
        {checkInUpdateTime && (
          <Card className={classes.card}>
            <div className={classes.patientName}>
              {getPatientFullName(patient)}
            </div>
            <div className={classes.age}>
              {`${typeof dob === "number" && getAge(dob)} years / ${getGender(
                gender,
              )}`}
            </div>
            <DataField name='Event' data={event?.name} />
            <DataField
              name='Check-in Date'
              data={
                checkInUpdateTime
                  ? getFormattedDateTime(checkInUpdateTime)
                  : "Not checked-in"
              }
            />
          </Card>
        )}
        {encounterDetails ? (
          encounterDetails.map((encounterDetail, idx) => (
            <ClosableBox
              name={getService(encounterDetail.service)}
              defaultState
              key={idx}>
              <div className={classes.service}>
                {encounter?.status === "SERVICE_ADMINISTERED" && (
                  <>
                    <div className={classes.medications}>
                      Medication Administered:
                    </div>
                    <div className={classes.medicationsAdministered}>
                      {getMedication(
                        encounterDetail.administration?.medicationAdministered,
                      )}
                    </div>

                    {encounterDetail?.administration?.screeningResponses
                      ?.length ? (
                      <>
                        <DataField name='Screening Response:' data={""} />
                        <div className={classes.screeningQuesContain}>
                          {encounterDetail?.administration?.screeningResponses?.map(
                            (response, idx) => (
                              <div>
                                <div className={classes.screeningQuestion}>
                                  Q{idx + 1}:{" "}
                                  {
                                    screeningQuestions.get(response?.questionId)
                                      ?.question
                                  }
                                </div>
                                <div>
                                  Answer:{" "}
                                  {!!response?.responseText
                                    ? response.responseText
                                    : getResponse(
                                        response.responseIdx,
                                        response?.questionId,
                                      )}
                                </div>
                                {!!response.reasonText && (
                                  <div>Reason: {response.reasonText}</div>
                                )}
                              </div>
                            ),
                          )}
                        </div>
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                )}
                {encounter?.status === "UNDER_OBSERVATION" && (
                  <>
                    <div className={classes.observations}>Observations:</div>
                    {!!encounterDetail.observations?.bloodGlucose && (
                      <DataField
                        name='Blood Glucose:'
                        data={
                          !!encounterDetail.observations?.bloodGlucose?.length
                            ? `${
                                encounterDetail.observations.bloodGlucose[
                                  encounterDetail.observations.bloodGlucose
                                    .length - 1
                                ].value
                              } ${getBloodGlucoseUnit(
                                encounterDetail.observations.bloodGlucose[
                                  encounterDetail.observations.bloodGlucose
                                    .length - 1
                                ].unit,
                              )}`
                            : "Cleared"
                        }
                      />
                    )}
                    {!!encounterDetail.observations?.bloodPressure && (
                      <DataField
                        name='Blood Pressure:'
                        data={
                          <div className={classes.bloodPressure}>
                            <div>
                              {!!encounterDetail.observations?.bloodPressure
                                ?.length
                                ? `${
                                    encounterDetail.observations.bloodPressure[
                                      encounterDetail.observations.bloodPressure
                                        .length - 1
                                    ].systolic
                                  }/${
                                    encounterDetail.observations.bloodPressure[
                                      encounterDetail.observations.bloodPressure
                                        .length - 1
                                    ].diastolic
                                  } mmHg`
                                : "Cleared"}
                            </div>
                          </div>
                        }
                      />
                    )}
                    {!!encounterDetail.observations?.pulse && (
                      <DataField
                        name='Pulse:'
                        data={
                          !!encounterDetail.observations?.pulse?.length
                            ? `${
                                encounterDetail.observations.pulse[
                                  encounterDetail.observations.pulse.length - 1
                                ].value
                              } beats/min`
                            : "Cleared"
                        }
                      />
                    )}
                    {!!encounterDetail.observations?.respiratoryRate && (
                      <DataField
                        name='Respiratory Rate:'
                        data={
                          !!encounterDetail.observations?.respiratoryRate
                            ?.length
                            ? `${
                                encounterDetail.observations.respiratoryRate[
                                  encounterDetail.observations.respiratoryRate
                                    .length - 1
                                ].value
                              } breaths/min`
                            : "Cleared"
                        }
                      />
                    )}
                    {!!encounterDetail.observations?.spO2 && (
                      <DataField
                        name='SpO2:'
                        data={
                          !!encounterDetail.observations?.spO2?.length
                            ? `${
                                encounterDetail.observations.spO2[
                                  encounterDetail.observations.spO2.length - 1
                                ].value
                              }%`
                            : "Cleared"
                        }
                      />
                    )}
                    {!!encounterDetail.observations?.temperature && (
                      <DataField
                        name='Temperature:'
                        data={
                          !!encounterDetail.observations?.temperature?.length
                            ? `${
                                encounterDetail.observations.temperature[
                                  encounterDetail.observations.temperature
                                    .length - 1
                                ].value
                              } ${getTemperatureUnit(
                                encounterDetail.observations.temperature[
                                  encounterDetail.observations.temperature
                                    .length - 1
                                ].unit,
                              )}`
                            : "Cleared"
                        }
                      />
                    )}
                    {!!encounterDetail.observations?.heightAndWeight
                      ?.height && (
                      <DataField
                        name='Height:'
                        data={
                          encounterDetail.observations?.heightAndWeight
                            ?.height >= 0
                            ? `${encounterDetail.observations.heightAndWeight.height} inches`
                            : "Cleared"
                        }
                      />
                    )}
                    {!!encounterDetail.observations?.heightAndWeight
                      ?.weight && (
                      <DataField
                        name='Weight:'
                        data={
                          encounterDetail.observations?.heightAndWeight
                            ?.weight >= 0
                            ? `${encounterDetail.observations.heightAndWeight.weight} lbs`
                            : "Cleared"
                        }
                      />
                    )}
                    {!!encounterDetail.observations?.leadTest && (
                      <DataField
                        name='Questionnaire Administered?:'
                        data={
                          encounterDetail.observations?.leadTest
                            ?.questionnaireAdministered
                            ? MultipleChoice.Yes
                            : MultipleChoice.No
                        }
                      />
                    )}
                    {!!encounterDetail.observations?.leadTest && (
                      <DataField
                        name='Blood Test Indicated?:'
                        data={
                          encounterDetail.observations?.leadTest
                            ?.bloodTestIndicated
                            ? MultipleChoice.Yes
                            : MultipleChoice.No
                        }
                      />
                    )}
                    {!!encounterDetail.observations?.leadTest &&
                      encounterDetail.observations?.leadTest
                        ?.bloodTestIndicated && (
                        <DataField
                          name='Blood Test Date:'
                          data={`${getFormattedDate(
                            encounterDetail.observations?.leadTest
                              ?.bloodTestDate,
                          )}`}
                        />
                      )}
                    {!!encounterDetail.observations?.leadTest &&
                      encounterDetail.observations?.leadTest
                        ?.bloodTestIndicated && (
                        <DataField
                          name='Result:'
                          data={
                            encounterDetail.observations?.leadTest
                              ?.capillaryTestResult
                          }
                        />
                      )}
                    {!!encounterDetail.observations?.symptoms && (
                      <>
                        <div className={classes.symptoms}>Symptoms:</div>
                        <div className={classes.symptomsDescription}>
                          {encounterDetail.observations?.symptoms?.length > 0
                            ? getSymptoms(
                                encounterDetail.observations?.symptoms,
                              )
                            : "Cleared"}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </ClosableBox>
          ))
        ) : (
          <></>
        )}
        <>
          {encounter?.status === "DISCHARGED" && (
            <ClosableBox name='Discharge' defaultState>
              <DataField name='Disposition:' data={getDisposition()} />
              <DataField
                name='Incentives:'
                data={
                  <>
                    {dischargeIncentives.length ? (
                      dischargeIncentives.map((incentive, idx) => (
                        <div key={idx}>
                          {incentive.description}
                          {incentive.serialNumber
                            ? ` (${incentive.serialNumber})`
                            : ""}
                        </div>
                      ))
                    ) : (
                      <>None</>
                    )}
                  </>
                }
              />
            </ClosableBox>
          )}
        </>
        <>
          {!!updatedPatientData?.id && (
            <Card className={classes.card}>
              <div>
                {!!updatedPatientData?.firstName ? (
                  <DataField
                    name='Patient Name:'
                    data={`${updatedPatientData?.firstName} ${updatedPatientData?.middleName} ${updatedPatientData?.lastName}`}
                  />
                ) : (
                  <DataField
                    name='Patient Name:'
                    data={getPatientFullName(patient)}
                  />
                )}
                {!!updatedPatientData?.dob && (
                  <DataField
                    name='Date Of Birth:'
                    data={
                      typeof updatedPatientData?.dob === "number"
                        ? getFormattedDob(
                            addLocalOffsetToTimestamp(updatedPatientData?.dob),
                          )
                        : ""
                    }
                  />
                )}
                {!!updatedPatientData?.gender && (
                  <DataField
                    name='Gender:'
                    data={getGender(updatedPatientData?.gender)}
                  />
                )}
                {!!updatedPatientData?.race && (
                  <DataField
                    name='Race:'
                    data={getRace(updatedPatientData?.race)}
                  />
                )}
                {!!updatedPatientData?.ethnicity && (
                  <DataField
                    name='Ethnicity:'
                    data={getEthnicity(updatedPatientData?.ethnicity)}
                  />
                )}
                {!!updatedPatientData?.address && (
                  <DataField
                    name='Address:'
                    data={updatedPatientData?.address}
                  />
                )}
                {!!updatedPatientData?.zipCode && (
                  <DataField
                    name='Zip Code:'
                    data={updatedPatientData?.zipCode}
                  />
                )}
                {!!updatedPatientData?.city && (
                  <DataField name='City:' data={updatedPatientData?.city} />
                )}
                {!!updatedPatientData?.country && (
                  <DataField
                    name='Country:'
                    data={updatedPatientData?.country}
                  />
                )}
                {!!updatedPatientData?.state && (
                  <DataField name='State:' data={updatedPatientData?.state} />
                )}
                {typeof updatedPatientData?.haveInsurance === "boolean" && (
                  <DataField
                    name='Have insurance:'
                    data={
                      updatedPatientData?.haveInsurance
                        ? InsuranceStatus.Yes
                        : InsuranceStatus.No
                    }
                  />
                )}
                {!!updatedPatientData?.insuranceDetails?.providerName && (
                  <DataField
                    name='Insurance Company:'
                    data={updatedPatientData?.insuranceDetails?.providerName}
                  />
                )}
                {!!updatedPatientData?.insuranceDetails?.policyNumber && (
                  <DataField
                    name='Policy Number:'
                    data={updatedPatientData?.insuranceDetails?.policyNumber}
                  />
                )}
                {!!updatedPatientData?.insuranceDetails?.groupNumber && (
                  <DataField
                    name='Group Number:'
                    data={updatedPatientData?.insuranceDetails?.groupNumber}
                  />
                )}
                {typeof updatedPatientData?.isPrimaryMember === "boolean" && (
                  <DataField
                    name='Is primary member:'
                    data={
                      updatedPatientData?.isPrimaryMember
                        ? InsuranceStatus.Yes
                        : InsuranceStatus.No
                    }
                  />
                )}
                {!!updatedPatientData?.primaryMemberName && (
                  <DataField
                    name='Primary Member Name:'
                    data={updatedPatientData?.primaryMemberName}
                  />
                )}
                {!!updatedPatientData?.primaryMemberDob && (
                  <DataField
                    name='Primary Member Date Of Birth:'
                    data={
                      typeof updatedPatientData?.primaryMemberDob === "number"
                        ? getFormattedDob(
                            addLocalOffsetToTimestamp(
                              updatedPatientData?.primaryMemberDob,
                            ),
                          )
                        : ""
                    }
                  />
                )}
                {typeof updatedPatientData?.haveSecondaryInsurance ===
                  "boolean" && (
                  <DataField
                    name='Have secondary insurance:'
                    data={
                      updatedPatientData?.haveSecondaryInsurance
                        ? InsuranceStatus.Yes
                        : InsuranceStatus.No
                    }
                  />
                )}
                {!!updatedPatientData?.secondaryInsuranceDetails
                  ?.providerName && (
                  <DataField
                    name='Insurance Company (secondary):'
                    data={
                      updatedPatientData?.secondaryInsuranceDetails
                        ?.providerName
                    }
                  />
                )}
                {!!updatedPatientData?.secondaryInsuranceDetails
                  ?.policyNumber && (
                  <DataField
                    name='Policy Number (secondary):'
                    data={
                      updatedPatientData?.secondaryInsuranceDetails
                        ?.policyNumber
                    }
                  />
                )}
                {!!updatedPatientData?.secondaryInsuranceDetails
                  ?.groupNumber && (
                  <DataField
                    name='Group Number (secondary):'
                    data={
                      updatedPatientData?.secondaryInsuranceDetails?.groupNumber
                    }
                  />
                )}
              </div>
            </Card>
          )}
        </>
      </div>
    </div>
  );
};

export default PatientDataDrawer;
